import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Select, message, Popconfirm, Space, Alert, Card } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { divisionService, divisionSiteService } from '@iso/services';

const { Option } = Select;

const DivisionSitesManagement = ({ corporateGroupId }) => {
  const [divisions, setDivisions] = useState([]);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [divisionSites, setDivisionSites] = useState([]);
  const [availableProperties, setAvailableProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (corporateGroupId) {
      loadDivisions();
    }
  }, [corporateGroupId]);

  useEffect(() => {
    if (selectedDivision) {
      loadDivisionSites(selectedDivision);
      loadAvailableProperties(selectedDivision);
    }
  }, [selectedDivision]);

  const loadDivisions = async () => {
    try {
      const response = await divisionService.getList(corporateGroupId, false);
      if (response && response.code === '0000') {
        setDivisions(response.divisions || []);
        if (response.divisions && response.divisions.length > 0) {
          setSelectedDivision(response.divisions[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading divisions:', error);
      message.error('Failed to load divisions');
    }
  };

  const loadDivisionSites = async (divisionId) => {
    try {
      setLoading(true);
      const response = await divisionSiteService.getList(divisionId);
      if (response && response.code === '0000') {
        setDivisionSites(response.division_sites || []);
      }
    } catch (error) {
      console.error('Error loading division sites:', error);
      message.error('Failed to load division sites');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableProperties = async (divisionId) => {
    try {
      const response = await divisionSiteService.getAvailableProperties(divisionId);
      if (response && response.code === '0000') {
        setAvailableProperties(response.properties || []);
      }
    } catch (error) {
      console.error('Error loading available properties:', error);
    }
  };

  const handleAdd = () => {
    form.resetFields();
    setModalVisible(true);
  };

  const handleDelete = async (divisionSiteId) => {
    try {
      const response = await divisionSiteService.destroy(divisionSiteId);
      if (response && response.code === '0000') {
        message.success('Property removed from division successfully');
        loadDivisionSites(selectedDivision);
        loadAvailableProperties(selectedDivision);
      } else {
        message.error(response.message || 'Failed to remove property from division');
      }
    } catch (error) {
      console.error('Error removing property from division:', error);
      message.error('Failed to remove property from division');
    }
  };

  const handleSubmit = async (values) => {
    try {
      const response = await divisionSiteService.batchCreate(
        selectedDivision,
        values.property_ids
      );

      if (response && response.code === '0000') {
        message.success(`${response.created_sites?.length || 0} property(ies) assigned successfully`);
        setModalVisible(false);
        form.resetFields();
        loadDivisionSites(selectedDivision);
        loadAvailableProperties(selectedDivision);
      } else {
        message.error(response.message || 'Failed to assign properties');
      }
    } catch (error) {
      console.error('Error assigning properties:', error);
      message.error('Failed to assign properties');
    }
  };

  const columns = [
    {
      title: 'Property Name',
      dataIndex: 'property_name',
      key: 'property_name',
      sorter: (a, b) => (a.property_name || '').localeCompare(b.property_name || ''),
    },
    {
      title: 'Property Address',
      dataIndex: 'property_address',
      key: 'property_address',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Popconfirm
            title="Remove this property from the division? Its contact person and hub at that property are deleted too, and contractors there can no longer choose this division."
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Remove
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (!corporateGroupId) {
    return (
      <Alert
        message="No Corporate Group Selected"
        description="Please select a corporate group in the General Settings tab to manage division sites."
        type="warning"
        showIcon
      />
    );
  }

  if (divisions.length === 0) {
    return (
      <Alert
        message="No Divisions Available"
        description="Please create divisions in the Divisions tab before assigning properties to them."
        type="info"
        showIcon
      />
    );
  }

  return (
    <div>
      <Card
        title="Select Division"
        style={{ marginBottom: 16 }}
        size="small"
      >
        <Select
          style={{ width: '100%' }}
          placeholder="Select a division"
          value={selectedDivision}
          onChange={setSelectedDivision}
        >
          {divisions.map(division => (
            <Option key={division.id} value={division.id}>
              {division.name}
            </Option>
          ))}
        </Select>
      </Card>

      <div style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
          disabled={!selectedDivision || availableProperties.length === 0}
          style={{ backgroundColor: '#0b2a1b', borderColor: '#0b2a1b', color: '#fff' }}
        >
          Assign Properties
        </Button>
        {availableProperties.length === 0 && selectedDivision && (
          <span style={{ marginLeft: 8, color: '#999' }}>
            All properties in this corporate group have been assigned
          </span>
        )}
      </div>

      <Table
        columns={columns}
        dataSource={divisionSites}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        locale={{
          emptyText: selectedDivision
            ? 'No properties assigned to this division yet'
            : 'Please select a division'
        }}
      />

      <Modal
        title="Assign Properties to Division"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="property_ids"
            label="Select Properties"
            rules={[
              { required: true, message: 'Please select at least one property' },
            ]}
          >
            <Select
              mode="multiple"
              placeholder="Select properties to assign to this division"
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {availableProperties.map(property => (
                <Option key={property.id} value={property.id}>
                  {property.name} - {property.address}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setModalVisible(false);
                form.resetFields();
              }}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                style={{ backgroundColor: '#0b2a1b', borderColor: '#0b2a1b' }}
              >
                Assign
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DivisionSitesManagement;
